import React from "react";
import { Link } from "react-router-dom";
import { Play, ArrowUpRight } from "lucide-react";
import Signature from "./Signature";
import { APPLICATION_LINK_PROPS } from "../config/applicationForm";

// Placeholder portrait — user will upload their own image later.
// A library/bookshelf composition mood image that approximates the reference.
const PORTRAIT_URL =
  "https://customer-assets.emergentagent.com/job_hero-design-6/artifacts/oiuyhizp_fad0e5ea-1712-413b-9f1d-c05784213e9c.png";

const Hero = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden hero-vignette grain"
    >
      {/* Portrait image — right side, bleeds to edge */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute right-0 top-0 bottom-0 w-full md:w-[62%] lg:w-[58%]">
          <img
            src={PORTRAIT_URL}
            alt="Iyare Omoruyi — educator, speaker, Murabbi"
            data-testid="hero-portrait-image"
            className="w-full h-full object-cover"
            style={{
              objectPosition: "60% center",
              maskImage:
                "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.35) 14%, #000 38%, #000 100%)",
              WebkitMaskImage:
                "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.35) 14%, #000 38%, #000 100%)",
            }}
          />
          {/* Bottom fade so portrait melts into pillars strip */}
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-[#0A1A2F]" />
        </div>
      </div>

      {/* Content */}
      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 pt-28 lg:pt-32 pb-16 lg:pb-20 min-h-[92vh] flex">
        <div className="w-full lg:w-[58%] reveal-stagger">
          {/* Cohort urgency banner */}
          <a
            {...APPLICATION_LINK_PROPS}
            data-testid="hero-cohort-banner"
            className="group inline-flex items-center gap-3 border border-azure/40 hover:border-azure bg-azure/10 hover:bg-azure/15 backdrop-blur-sm px-4 py-2 mb-7 transition-all duration-300"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold/60 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-gold" />
            </span>
            <span className="text-[10.5px] tracking-[0.32em] uppercase text-white/85 group-hover:text-white">
              Now accepting parent intake forms
            </span>
            <span className="text-[10.5px] tracking-[0.32em] uppercase text-azure-soft group-hover:text-white inline-flex items-center gap-1">
              Apply
              <ArrowUpRight className="w-3 h-3 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.8} />
            </span>
          </a>

          <h1
            data-testid="hero-headline"
            className="font-serif text-white text-[32px] sm:text-[44px] lg:text-[56px] xl:text-[64px] leading-[1.02] tracking-[-0.01em]"
          >
            Teaching <span className="text-azure-soft italic-soft">Minds.</span>
            <br />
            Cultivating <span className="text-azure-soft">Souls.</span>
          </h1>

          {/* Gold accent rule */}
          <div className="mt-8 h-px w-20 bg-gold" aria-hidden="true" />

          <p
            data-testid="hero-subtitle"
            className="mt-8 text-white/75 text-base lg:text-[17px] leading-[1.85] max-w-xl"
          >
            Muslim educator, speaker, and mentor helping individuals and communities
            through transformative Islamic education and the Murabbi framework.
          </p>

          {/* CTAs */}
          <div className="mt-12 flex flex-col sm:flex-row gap-4">
            <Link
              to="/podcast"
              data-testid="hero-watch-podcast-btn"
              className="group inline-flex items-center justify-center gap-3 bg-azure hover:bg-[#5C9BEC] text-white px-7 py-3.5 text-[11px] tracking-[0.32em] uppercase font-medium transition-all duration-300"
            >
              <Play className="w-3.5 h-3.5 fill-white" strokeWidth={0} />
              Watch the Podcast
            </Link>
            <Link
              to="/speaking"
              data-testid="hero-book-speak-btn"
              className="inline-flex items-center justify-center border border-white/40 hover:border-gold hover:text-gold text-white px-7 py-3.5 text-[11px] tracking-[0.32em] uppercase font-medium transition-all duration-300"
            >
              Book Me to Speak
            </Link>
          </div>

          {/* Quote block */}
          <div className="mt-16 max-w-md">
            <div className="flex gap-4">
              <span className="font-serif text-azure-soft text-[44px] leading-none -mt-2 select-none">
                “
              </span>
              <div>
                <p className="text-white/80 text-[15px] leading-[1.85]">
                  The role of the Murabbi is not to fill minds with information,
                  but to <span className="text-azure-soft">awaken</span> hearts
                  to their purpose.
                </p>
                <div className="mt-5">
                  <Signature className="h-12" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
